import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const IMAGES = '/images/branding-styleguide-imagery';

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
      {
        title: 'Section 508 ICT Testing Baseline',
        subtitle: '[ictbaseline.access-board.gov]',
        linkUrl: REFERENCE_LINKS.ictTestingBaseline,
      },
    ],
  },
];

const newsItems = [
  {
    image: `${IMAGES}/news-people-focused@2x.webp`,
    date: '02/09/24',
    title: 'People-focused — center the people we serve',
    text: 'Center the individuals and communities we serve — real people in real situations, not press moments or buildings.',
  },
  {
    image: `${IMAGES}/news-relatable@2x.webp`,
    date: '01/14/24',
    title: 'Relatable — real people in real moments',
    text: 'Choose warm, candid, relatable imagery that feels authentic and human — avoid staged or corporate stock photography.',
  },
  {
    image: `${IMAGES}/news-diverse@2x.webp`,
    date: '09/07/23',
    title: 'Diverse — reflect California’s communities',
    text: 'These are placeholders — replace with approved, people-focused photography from the Visual Identity Resources library.',
  },
];

type Example = { src: string; height: number; text: string };

// Each guideline: a heading, its text and a Do / Don't pair of screens (976px wide at 2x, shown at 488px).
const guidelines: {
  id: string;
  label: string;
  title: string;
  text: string;
  do: Example;
  dont: Example;
}[] = [
  {
    id: 'focal-point',
    label: 'Focal point',
    title: 'Have a focal point',
    text: "Ensure every image has a clear focal point that draws the viewer's attention and reinforces the message you want to convey. This helps guide the user’s eye and makes the visual content more impactful and purposeful.",
    do: {
      src: 'screen-cropping-image-01',
      height: 409,
      text: 'Establish a clear focal point when cropping an image.',
    },
    dont: {
      src: 'screen-cropping-image-02',
      height: 409,
      text: "Don't crop an image in a way that doesn't show a clear focal point.",
    },
  },
  {
    id: 'size',
    label: 'Size',
    title: 'Size appropriately',
    text: "Choose image dimensions that align with their purpose and placement within the design to maintain clarity and impact. Appropriately size images for different displays and platforms, balancing quality with performance. Resolution plays a key role in load speed, so keep it as low as possible without sacrificing visual fidelity. Test resolution sizes for specific ratios and devices to ensure images display sharply and don't appear pixelated, preserving both quality and network efficiency.",
    do: {
      src: 'screen-compromising-quality-01',
      height: 465,
      text: 'Export images at the lowest file size possible without compromising quality.',
    },
    dont: {
      src: 'screen-compromising-quality-02',
      height: 465,
      text: 'Avoid using small file sizes that may result in pixelation of the image.',
    },
  },
  {
    id: 'matching-imagery',
    label: 'Imagery',
    title: 'Matching Imagery',
    text: 'Ensure imagery aligns with the tone, message, and audience of the content it supports. Select visuals that reinforce the narrative and complement the overall design aesthetic. Consistent style, lighting, and color grading across imagery create a cohesive and professional look, enhancing the user experience and maintaining brand integrity.',
    do: {
      src: 'screen-matching-imagery-01',
      height: 435,
      // The frame's sentences describe a stop symbol and a destructive message, not the "Change the
      // template" dialog these screens show (DI-115); these describe the screens.
      text: 'Use imagery that corresponds with the messaging. Here the illustrations match their options: a dashboard for Widgets Only and a globe for Map First.',
    },
    dont: {
      src: 'screen-matching-imagery-02',
      height: 435,
      text: 'Avoid imagery that does not relate to the message. Here a sleeping clock says nothing about Widgets Only, and hands holding an award ribbon say nothing about Map First.',
    },
  },
  {
    id: 'accessibility',
    // The frame's Contents says "Matches" here, a leftover label (DI-093).
    label: 'Accessibility',
    title: 'Accessible Images',
    text: 'Accessible images are essential for ensuring an inclusive user experience. Use descriptive alt text to convey the purpose and content of images for users relying on screen readers. Avoid embedding critical text within images; instead, present it as live, readable text. Ensure sufficient contrast between images and their backgrounds to accommodate users with visual impairments.',
    // The frame pairs these the other way round (DI-095): the screen with the overlay sits under Don't
    // and the Do sentence is a Don't one; here the overlay screen is the Do and the plain one the Don't.
    do: {
      src: 'screen-accessible-images-02',
      height: 414,
      text: 'Use text over images that do have an overlay or scrim layer to add contrast between the text and image.',
    },
    dont: {
      src: 'screen-accessible-images-01',
      height: 414,
      text: 'Avoid placing text over images that have no overlay or scrim layer; without one the text loses contrast against the image.',
    },
  },
];

// The Contents entries are the text of the element that carries the id (react-scrollspy-ez reads its
// innerText). The frame's entries are shorter than the headings, so a zero-height anchor carries the
// short label and the heading itself has no id. Same approach as the Logos page (DI-105).
const NavAnchor = ({ id, label }: { id: string; label: string }) => (
  <span id={id} className="nav-anchor" aria-hidden="true">
    {label}
  </span>
);

const ImageryStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Imagery"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Imagery' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  Imagery is a powerful storytelling tool that enhances user engagement by conveying
                  emotions, ideas, and context visually. Thoughtfully chosen images complement the
                  content, create connections, and guide users through the experience, making
                  information more relatable and memorable.
                </p>
              </div>
              <div className="news">
                {newsItems.map((item) => (
                  <div className="news-item" key={item.title}>
                    <img className="news-img" src={item.image} alt="" width={290} height={200} />
                    <div className="news-desc">
                      <span className="news-data">{item.date}</span>
                      <strong className="news-title">{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="page-content-item">
                <NavAnchor id="photography" label="Photography" />
                <h3 className="h3">Photography Direction</h3>
                <p className="mb-40">
                  DHCS photography is people-focused — it centers the individuals and communities we
                  serve rather than buildings, events, or press moments. Choose imagery that feels
                  relatable and authentic: real people in real situations. Reflect the diversity of
                  California’s communities across age, race, ethnicity, ability, and circumstance,
                  and make sure the people shown represent the audiences the content is meant to
                  reach. Favor warm, candid moments over staged or corporate stock photography.
                </p>
              </div>
              <img
                src={`${IMAGES}/photography-direction@2x.webp`}
                alt="Photographs of people of different ages and backgrounds in everyday situations: families, couples, older adults, and a doctor with a child"
                width={1000}
                height={400}
                className="mb-40 img-responsive"
              />
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              {guidelines.map((guideline) => (
                <section key={guideline.id}>
                  <div className="page-content-item mb-40">
                    <NavAnchor id={guideline.id} label={guideline.label} />
                    <h3 className="h3">{guideline.title}</h3>
                    <p>{guideline.text}</p>
                  </div>
                  <div className="ex-cards">
                    <div className="ex-card true">
                      <img
                        className="ex-card-img"
                        src={`${IMAGES}/${guideline.do.src}@2x.webp`}
                        alt=""
                        width={488}
                        height={guideline.do.height}
                      />
                      <div className="ex-card-desc">
                        <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                          Do
                        </BadgeStyled>
                        <p>{guideline.do.text}</p>
                      </div>
                    </div>
                    <div className="ex-card false">
                      <img
                        src={`${IMAGES}/${guideline.dont.src}@2x.webp`}
                        alt=""
                        width={488}
                        height={guideline.dont.height}
                      />
                      <div className="ex-card-desc">
                        <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                          Don&apos;t
                        </BadgeStyled>
                        <p>{guideline.dont.text}</p>
                      </div>
                    </div>
                  </div>
                </section>
              ))}
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'photography',
                    'guidelines',
                    'focal-point',
                    'size',
                    'matching-imagery',
                    'accessibility',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/icons"
        previous="Iconography & Graphics"
        nextLink="/branding-styleguide/interactions-and-animations"
        next="Interactions and Animations"
      />
    </div>
  </div>
);

const Imagery = styled(ImageryStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 5, 6, 7, 8])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The Updated frame lays the three cards in one row inside a 40px padded box, at any width. */
  .news {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 2.5rem;
  }

  .news-img {
    width: 100%;
    height: 12.5rem;
    object-fit: cover;
  }

  .nav-anchor {
    display: block;
    height: 0;
    overflow: hidden;
  }
`;

export default Imagery;
